import React, { useCallback, useContext, useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import OtherContactService from "services/OtherContact.service";
import {
  validateEmail,
  validatePhoneNumber,
} from "shared/utils/validator.utils";
import InlineEditableTable from "web/components/form-elements/InlineEditableTable.component";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import { AuthContext } from "webContext/Auth.context";
import { OrganizationContext } from "webContext/Organization.context";
import { ToastContext } from "webContext/Toast.context";

const classes = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: "white",
    overflow: "auto",
  },
  banner: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "flex-end",
  },
  tableContainer: {
    flex: 1,
  },
  loadingText: {
    textAlign: "center",
    fontSize: 16,
    color: "#666",
    marginTop: 40,
  },
});

export default function OtherContactsScreen() {
  const {
    otherContactList,
    getOtherContacts,
    getLocations,
    getOrganizationSystems,
  } = useContext(OrganizationContext);
  const { showToast } = useContext(ToastContext);
  const { isMessagingManager } = useContext(AuthContext);

  const [localContacts, setLocalContacts] = useState([]);
  useEffect(() => {
    const sortedContacts = [...(otherContactList || [])].sort((a, b) => {
      const getLastName = (name) => {
        const nameParts = name.split(" ");
        return nameParts.length > 1 ? nameParts[nameParts.length - 1] : name;
      };
      return getLastName(a.name).localeCompare(getLastName(b.name));
    });

    setLocalContacts(
      sortedContacts.map((contact) => ({
        id: contact.otherContactId,
        name: contact.name,
        description: contact.description || "",
        organization: contact.organization || "",
        places: (contact._associations?.Locations || []).map(
          (loc) => loc.locationId,
        ),
        systems: (contact._associations?.Systems || []).map(
          (sys) => sys.organizationSystemId,
        ),
        phone: contact.phone || "",
        email: contact.email || "",
        backupContactId: contact.backupContactId || null,
        _rawContact: contact,
      })),
    );
  }, [otherContactList]);

  const fetchOtherContacts = useCallback(async () => {
    try {
      await Promise.all([
        getOtherContacts(),
        getLocations(),
        getOrganizationSystems(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [getOtherContacts, getLocations, getOrganizationSystems]);

  useEffect(() => {
    fetchOtherContacts();
  }, [fetchOtherContacts]);

  const handleRowUpdate = useCallback(
    async (contactId, updates) => {
      try {
        const profileUpdates = { ...updates };
        delete profileUpdates.places;
        delete profileUpdates.systems;
        let id = contactId;
        if (Object.keys(profileUpdates).length > 0) {
          const profileRes =
            await OtherContactService.updateOtherContactProfile(
              contactId,
              profileUpdates,
            );
          id = profileRes.payload.otherContactId;
        }

        if (updates.places !== undefined) {
          await OtherContactService.updateOtherContactLocations(
            id,
            updates.places,
          );
        }

        if (updates.systems !== undefined) {
          await OtherContactService.updateOtherContactSystems(
            id,
            updates.systems,
          );
        }
        await getOtherContacts();
        showToast("Contact updated successfully", "success");
      } catch (error) {
        console.error("Error updating contact:", error);
        showToast("Failed to update contact", "error");
      }
    },
    [getOtherContacts, showToast],
  );

  const tableData = localContacts;
  const handleAddNewContact = () => {
    setLocalContacts((prev) => [
      {
        id: "new",
        name: "",
        description: "",
        organization: "",
        places: [],
        systems: [],
        phone: "",
        email: "",
        backupContactId: null,
        _rawContact: {},
        isNew: true,
      },
      ...prev,
    ]);
  };

  const handleRemoveContact = async (id) => {
    try {
      if (id !== "new") {
        await OtherContactService.deleteOtherContact(id);
      }
      setLocalContacts((prev) => prev.filter((contact) => contact.id !== id));
      showToast("Contact deleted successfully", "success");
    } catch (error) {
      showToast("Failed to delete contact", "error");
      console.error("Error deleting contact:", error);
    }
  };

  const getLastName = useCallback((name) => {
    const nameParts = name.split(" ");
    return nameParts.length > 1 ? nameParts[nameParts.length - 1] : name;
  }, []);

  const columns = [
    {
      key: "name",
      label: "Name",
      type: "textRequired",
      editable: true,
      sortable: true,
      customSort: (a, b) => {
        return getLastName(a.name || "").localeCompare(
          getLastName(b.name || ""),
        );
      },
    },
    {
      key: "description",
      label: "Description",
      editable: true,
      type: "text",
      sortable: true,
    },
    {
      key: "organization",
      label: "Organization",
      editable: true,
      type: "text",
      sortable: true,
    },
    {
      key: "places",
      label: "Places",
      editable: true,
      type: "locations",
      idField: "locationId",
      displayField: "name",
    },
    {
      key: "systems",
      label: "Systems",
      editable: true,
      type: "systems",
      idField: "organizationSystemId",
      displayField: "title",
    },
    {
      key: "phone",
      label: "Phone",
      editable: true,
      type: "textValidation",
      validationFn: validatePhoneNumber,
      errorMessage: "Invalid phone number",
      isPhone: true,
    },
    {
      key: "email",
      label: "Email",
      editable: true,
      type: "textValidation",
      validationFn: validateEmail,
      errorMessage: "Invalid email address",
    },
    {
      key: "backupContactId",
      label: "Backup Contact",
      editable: true,
      type: "contacts",
      idField: "otherContactId",
      displayField: "displayName",
    },
    ...(isMessagingManager
      ? []
      : [{ key: "delete", editable: true, type: "action" }]),
  ];

  return (
    <View style={classes.container}>
      <View style={classes.banner}>
        <CustomButton
          title="Add Contact"
          variant="dark"
          onPress={handleAddNewContact}
          icon="plus-circle-outline"
        />
      </View>
      <InlineEditableTable
        data={tableData}
        columns={columns}
        searchPlaceholder="Search other contacts..."
        onRowUpdate={handleRowUpdate}
        onRemoveContact={handleRemoveContact}
      />
    </View>
  );
}
